import React from 'react';
import { Task } from '../../../types';
import { GuidedProgressBar } from './GuidedProgressBar';

export interface GuidedWelcomeProps {
  firstName: string;
  /** Number of tasks already submitted (0..total). Drives copy: 0 →
      first-visit "Welcome / Begin"; >0 → "Welcome back / Continue". */
  done: number;
  total: number;
  /** The task the participant will be taken to when they tap the CTA.
      Used to render the small preview chip above the CTA. */
  nextTask: Task | null;
  /** Researcher's intro message — `Project.welcomeMessage`. If empty,
      we render a neutral fallback so the layout never breaks. */
  researcherQuote?: string;
  /** Tapping Begin / Continue. */
  onBegin: () => void;
}

const DEFAULT_QUOTE = 'Take your time. We’ll go step by step.';

/** Returns an SVG icon for a task's media type — used inside the
    preview chip's coloured circle. Kept inline for simplicity; matches
    the visual language of the audio surface + capture screens. */
function MediaTypeIcon({ mediaType }: { mediaType?: string }) {
  const stroke = 'currentColor';
  const common = {
    width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke,
    strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  };
  switch (mediaType) {
    case 'audio':
      return (
        <svg {...common}>
          <rect x="9" y="3" width="6" height="12" rx="3" />
          <path d="M5 11v1a7 7 0 0 0 14 0v-1" />
          <line x1="12" y1="19" x2="12" y2="23" />
        </svg>
      );
    case 'photo':
      return (
        <svg {...common}>
          <path d="M5 7h4l1-2h4l1 2h4v12H5z" />
          <circle cx="12" cy="13" r="3.5" />
        </svg>
      );
    case 'video':
      return (
        <svg {...common}>
          <rect x="3" y="6" width="13" height="12" rx="2" />
          <path d="M16 10l5-3v10l-5-3z" />
        </svg>
      );
    case 'text':
      return (
        <svg {...common}>
          <path d="M5 5h14M5 10h14M5 15h9" />
        </svg>
      );
    case 'signification':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
}

function formatDuration(estSec?: number): string | null {
  if (!estSec || estSec <= 0) return null;
  const min = Math.round(estSec / 60);
  if (min < 1) return `${estSec} sec`;
  return `${min} min`;
}

export const GuidedWelcome: React.FC<GuidedWelcomeProps> = ({
  firstName,
  done,
  total,
  nextTask,
  researcherQuote,
  onBegin,
}) => {
  const isReturn = done > 0;
  const greetingPrefix = isReturn ? 'Welcome back,' : 'Welcome,';
  const ctaLabel = isReturn ? 'Continue' : 'Begin';
  const quote = researcherQuote?.trim() || DEFAULT_QUOTE;
  const mediaType = nextTask?.mediaTypes?.[0];
  const duration = formatDuration(nextTask?.estimatedDurationSec);
  // Position-in-sequence label — replaces the redundant media-type
  // tag in the chip (the ember icon already signals the media type).
  // "Up next · 1 of 9" reads naturally for both first-visit (0 done)
  // and return ("up next" is still the next-incomplete task).
  const positionLabel = `Up next · ${done + 1} of ${total}`;

  return (
    <div className="gd-screen">
      <div className="gd-progress-strip">
        <GuidedProgressBar total={total} current={done} />
      </div>

      <div className="gd-greeting">
        <h1>
          {greetingPrefix}
          <br />
          <span className="gd-name">{firstName || 'there'}.</span>
        </h1>
        <p className="gd-quote">&ldquo;{quote}&rdquo;</p>
      </div>

      <div className="gd-bottom">
        {nextTask && (
          <div className="gd-task-chip" aria-label={`${positionLabel}: ${nextTask.title || nextTask.prompt || 'Task'}`}>
            <span className="gd-tc-ic"><MediaTypeIcon mediaType={mediaType} /></span>
            <span className="gd-tc-meta">
              <span className="gd-tc-lbl">{positionLabel}</span>
              <span className="gd-tc-name">{nextTask.title || nextTask.prompt || 'Task'}</span>
            </span>
            {duration && <span className="gd-tc-dur">{duration}</span>}
          </div>
        )}
        <button className="gd-cta" onClick={onBegin} type="button">
          {ctaLabel}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
};
